import { useState, useEffect, useRef, Suspense, lazy } from 'react';

import classNames from 'classnames';

import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { getFeatureToggle } from '@helsenorge/framework-utils/hn-feature-toggle';
import { HeaderFooterEvents } from '@helsenorge/framework-utils/web-component/constants';
import type { FooterDataDetail } from '@helsenorge/framework-utils/web-component/events';
import type { WebCompProps } from '@helsenorge/framework-utils/web-component/register';
import useWebComponentEvents from '@helsenorge/framework-utils/web-component/useWebComponentEvents';

import { renderFooterColumn, renderSimplifiedFooterContent } from './footer-utils';
import { isReferrer } from '../../utils/referrer';
import CookieBannerContainer from '../cookie-banner';

const AmplitudeInitializer = lazy(() => import('./amplitude-initializer'));

import styles from './styles.module.scss';

export interface FooterWebCompProps extends WebCompProps {
  language?: LanguageLocales;
  hide?: string | boolean;
  hidecookiebanner?: string | boolean;
  simplified?: string | boolean;
  nomargin?: string | boolean;
  nopageviewtracking?: string;
}

const Footer: React.FC<FooterWebCompProps> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const { subscribe } = useWebComponentEvents('hn-webcomp-footer', ref);

  const [footerData, setFooterData] = useState<FooterDataDetail>();
  const [hiddenFooter, setHiddenFooter] = useState<boolean>(props.hide !== undefined && props.hide !== 'false');
  const [isSimplifiedFooter, setIsSimplifiedFooter] = useState<boolean>(
    isReferrer('oidc') || (props.simplified !== undefined && props.simplified !== 'false')
  );
  const [noMarginFooter, setNoMarginFooter] = useState<boolean>(props.nomargin !== undefined && props.nomargin !== 'false');
  const [simplifiedFooterText, setSimplifiedFooterText] = useState<string>();

  useEffect(() => {
    return subscribe('hn-webcomp-header-footer-event-setsimplifiedfooter', (event: CustomEvent) => {
      setIsSimplifiedFooter(event.detail.simplifiedFooter);
      setSimplifiedFooterText(event.detail.simplifiedFooterText);
    });
  }, []);

  useEffect(() => {
    return subscribe(HeaderFooterEvents.sethiddenfooter, (event: CustomEvent) => {
      setHiddenFooter(event.detail.hiddenFooter);
    });
  }, []);

  useEffect(() => {
    return subscribe(HeaderFooterEvents.setnomarginfooter, (event: CustomEvent) => {
      setNoMarginFooter(event.detail.noMarginFooter);
    });
  }, []);

  useEffect(() => {
    return subscribe(HeaderFooterEvents.setfooterdata, (event: CustomEvent<FooterDataDetail>) => {
      setFooterData(event.detail);
    });
  }, []);

  const shouldInitAmplitude = getFeatureToggle('AmplitudeEnabled');

  const hideCookieBanner = props.hidecookiebanner === 'true';
  const language = footerData?.language || props.language || LanguageLocales.NORWEGIAN;

  const footerClassNames = classNames(styles.footer, {
    [styles['footer--normal']]: !isSimplifiedFooter,
    [styles['footer--no-margin']]: noMarginFooter,
  });
  const containerSimplifiedClassNames = classNames(styles['footer__container'], styles['footer__container--simplified']);
  const elementClassNames = classNames(
    styles['footer__element'],
    styles['footer__element--right'],
    styles['footer__element--simplified'],
    styles['footer__element__text--simplified'],
    styles['footer__element__text--large']
  );
  const footerColumnsClassNames = classNames(styles['footer__column--managing-editor']);

  const lazyAmplitudeInitializer = shouldInitAmplitude && (
    <Suspense fallback={null}>
      <AmplitudeInitializer noPageViewTracking={props.nopageviewtracking === 'true'} />
    </Suspense>
  );

  if (hiddenFooter) {
    return <div ref={ref}>{lazyAmplitudeInitializer}</div>;
  }

  return (
    <div ref={ref}>
      {lazyAmplitudeInitializer}
      <footer className={footerClassNames} aria-label={footerData?.footerDTO?.fasteTekster?.footerAriaLabel ?? 'Bunnmeny'} lang={language}>
        {isSimplifiedFooter ? (
          <div className={containerSimplifiedClassNames}>
            <div>{renderSimplifiedFooterContent(simplifiedFooterText, footerData?.footerDTO?.footerMenu?.supportHotline)}</div>
            <div>
              <div className={elementClassNames}>{footerData?.footerDTO?.footerMenu?.managingEditor}</div>
            </div>
          </div>
        ) : (
          <div className={styles['footer__container']}>
            {footerData?.footerDTO?.footerMenu?.footerColumns?.map((footerColumn, index: number) => {
              return renderFooterColumn(
                footerColumn,
                index,
                footerData?.footerDTO?.footerMenu?.supportHotline,
                footerData?.footerDTO?.footerMenu?.languageLinkList,
                footerData?.footerDTO?.footerMenu?.showCookieBannerText
              );
            })}
            <div className={footerColumnsClassNames}>
              {footerData?.footerDTO?.footerMenu?.managingEditor && (
                <div className={styles.footer__column__innerwrapper}>
                  <div
                    className={classNames(styles.footer__element, styles['footer__element__text'], styles['footer__element__text--large'])}
                  >
                    {footerData?.footerDTO?.footerMenu?.managingEditor}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </footer>
      <CookieBannerContainer hideInitially={hideCookieBanner} language={language} />
    </div>
  );
};

export default Footer;
